import { useCallback, useState } from "react";

export type ModalContentState = {
  modalTitle?: string;
  dialogWidth?: string | number;
  openDialog?: () => void;
  closeDialog?: () => void;
  modalContent?: any;
  modalComponent?: any;
  modalProps?: any;
  dialogFooterContent?: any;
  errorModal?: boolean;
  hideClose?: boolean;
} | null;

type HistoryState = {
  history: ModalContentState[];
  index: number; // -1 means none
};

export function useModalHistory(initial?: ModalContentState) {
  const [state, setState] = useState<HistoryState>(() => {
    if (!initial) return { history: [], index: -1 };
    return { history: [initial], index: 0 };
  });

  const current = state.index >= 0 ? state.history[state.index] : null;

  // useCallback throughout: these functions are served through ModalContext,
  // so their identities must survive Router re-renders (resize/auth state)
  // or every consumer — including the rAF-driven GL chart — re-renders per
  // tick. All bodies use functional setState, so the dep arrays are empty.
  const open = useCallback((next: ModalContentState) => {
    setState((prev) => {
      const truncated = prev.history.slice(0, prev.index + 1);
      const newHistory = [...truncated, next];
      return { history: newHistory, index: newHistory.length - 1 };
    });
  }, []);

  // Replace the current modal state without pushing a new history entry
  const replace = useCallback(
    (
      next:
        | ModalContentState
        | ((prev: ModalContentState) => ModalContentState),
    ) => {
      setState((prev) => {
        if (prev.index === -1) {
          // No current entry; behave like open
          const value = typeof next === "function" ? (next as any)(null) : next;
          const newHistory = [value];
          return { history: newHistory, index: 0 };
        }
        const before = prev.history.slice(0, prev.index);
        const after = prev.history.slice(prev.index + 1);
        const current = prev.history[prev.index];
        const value =
          typeof next === "function" ? (next as any)(current) : next;
        const newHistory = [...before, value, ...after];
        return { history: newHistory, index: prev.index };
      });
    },
    [],
  );

  const back = useCallback(() => {
    setState((prev) => {
      if (prev.index <= 0) return prev;
      return { ...prev, index: prev.index - 1 };
    });
  }, []);

  const forward = useCallback(() => {
    setState((prev) => {
      if (prev.index === -1 || prev.index >= prev.history.length - 1)
        return prev;
      return { ...prev, index: prev.index + 1 };
    });
  }, []);

  const close = useCallback(() => {
    setState({ history: [], index: -1 });
  }, []);

  const setHistory = useCallback((updater: any) => {
    setState((prev) => {
      const currentHistory = prev.history;
      const nextHistory =
        typeof updater === "function" ? updater(currentHistory) : updater;
      const nextIndex =
        nextHistory && nextHistory.length ? nextHistory.length - 1 : -1;
      return { history: nextHistory ?? [], index: nextIndex };
    });
  }, []);
  return {
    current,
    open,
    replace,
    back,
    forward,
    close,
    canGoBack: state.index > 0,
    canGoForward: state.index >= 0 && state.index < state.history.length - 1,
    history: state.history,
    setHistory,
  };
}
